/**
 * NF525 — Evening reminder toast.
 *
 * Fires once per day between 22h30 and 23h59 local time, only if:
 * - There is sales activity today AND
 * - No Z closure has been performed today yet.
 *
 * Uses localStorage to dedupe per day. Purely frontend — does not block.
 * The hook is mounted in AppLayout so it runs while the POS is open.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useEffectiveTenantId } from './useTenant';

const STORAGE_KEY_PREFIX = 'nf525_evening_toast_last_';

function todayKey(tenantId: string): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${STORAGE_KEY_PREFIX}${tenantId}_${y}-${m}-${d}`;
}

function isEveningWindow(): boolean {
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes();
  // 22:30 → 23:59
  return h === 22 ? m >= 30 : h === 23;
}

export function useZClosureEveningToast() {
  const tenantId = useEffectiveTenantId();
  const checkedAt = useRef<number>(0);

  useEffect(() => {
    if (!tenantId) return;

    const check = async () => {
      // Debounce: re-check at most every 5 min
      const now = Date.now();
      if (now - checkedAt.current < 5 * 60 * 1000) return;
      checkedAt.current = now;

      if (!isEveningWindow()) return;

      const key = todayKey(tenantId);
      if (typeof localStorage !== 'undefined' && localStorage.getItem(key)) return;

      // Start of today (local)
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);

      // 1. Any Z closure today?
      const { data: zToday } = await supabase
        .from('z_closures')
        .select('id')
        .eq('tenant_id', tenantId)
        .gte('created_at', startOfDay.toISOString())
        .limit(1)
        .maybeSingle();
      if (zToday) return; // déjà clôturé

      // 2. Any sales today?
      const { count } = await supabase
        .from('sales')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .eq('status', 'completed')
        .gte('created_at', startOfDay.toISOString());
      if (!count || count === 0) return; // pas d'activité

      // 3. Fire toast + dedupe
      toast.warning('Clôture Z journalière', {
        description: `${count} ticket(s) aujourd'hui. Pensez à votre clôture Z avant la fin de journée (obligation fiscale auto-cert).`,
        duration: 15000,
      });
      try {
        localStorage.setItem(key, String(Date.now()));
      } catch {
        /* ignore */
      }
    };

    check();
    const interval = setInterval(check, 60 * 1000); // check every minute
    return () => clearInterval(interval);
  }, [tenantId]);
}
